import React from 'react';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'success' | 'warning';
  disabled?: boolean;
  tooltip?: string;
}

interface QuickActionsBarProps {
  actions: QuickAction[];
  moreActions?: QuickAction[];
}

const QuickActionsBar: React.FC<QuickActionsBarProps> = ({ actions, moreActions }) => {
  const [showMore, setShowMore] = React.useState(false);

  const getVariantClasses = (variant: QuickAction['variant'] = 'secondary') => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600';
      case 'success':
        return 'bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600';
      case 'warning':
        return 'bg-amber-600 text-white hover:bg-amber-700 border-amber-600';
      case 'secondary':
      default:
        return 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300';
    }
  };

  return (
    <div className="sticky top-0 z-10 bg-white border-b border-gray-200 -mx-6 px-6 py-4">
      <div className="flex items-center gap-2 flex-wrap">
        {actions.map((action) => (
          <button
            key={action.id}
            onClick={action.onClick}
            disabled={action.disabled}
            title={action.tooltip}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${getVariantClasses(action.variant)}`}
          >
            {action.icon}
            <span>{action.label}</span>
          </button>
        ))}

        {moreActions && moreActions.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowMore(!showMore)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border bg-white text-gray-700 hover:bg-gray-50 border-gray-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
              <span>More</span>
            </button>

            {showMore && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMore(false)}
                />
                
                {/* Dropdown */}
                <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                  <div className="py-1">
                    {moreActions.map((action) => (
                      <button
                        key={action.id}
                        onClick={() => {
                          action.onClick();
                          setShowMore(false);
                        }}
                        disabled={action.disabled}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="text-gray-400">{action.icon}</span>
                        <span>{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickActionsBar;
